// Step 5 of the client-side X12 pipeline (844 variant): walks one
// transaction's flat body segments (from step 2's envelope tree) into the
// loop structure a human reader thinks in — header, Name loops, and
// PAD-style claim/adjustment-detail loops. Only ever runs against a
// transaction whose ST01 is "844"; anything else is left alone so it can
// still be shown, read-only, by the UI. Unlike 840/843/850/852/856, the 844
// has no summary segment set at all (no CTT-style count to cross-check) and
// its PAD detail loop never nests its own Name sub-loop, REF or DTM — those
// are header-only. Mirrors parser843.ts's pattern, minus the summary phase
// and the item Name sub-loop.

import type {EdiSegment} from './tokenizer';
import type {EdiEnvelope, EdiGroup, EdiTransaction} from './envelope';

export interface Header844 {
  baa?: EdiSegment;
  con?: EdiSegment;
  refs: EdiSegment[];
  dtm: EdiSegment[];
  other: EdiSegment[];
}

export interface NameLoop844 {
  n1: EdiSegment;
  n2?: EdiSegment;
  n3?: EdiSegment;
  n4?: EdiSegment;
  n5?: EdiSegment;
  n6?: EdiSegment;
  n9: EdiSegment[];
  refs: EdiSegment[];
  per?: EdiSegment;
  other: EdiSegment[];
}

export interface ItemLoop844 {
  pad: EdiSegment;
  qty: EdiSegment[];
  amt: EdiSegment[];
  ctp?: EdiSegment;
  other: EdiSegment[];
}

export interface Parsed844 {
  header: Header844;
  nameLoops: NameLoop844[];
  items: ItemLoop844[];
}

function attachHeaderSegment(header: Header844, s: EdiSegment): void {
  switch (s.id) {
    case 'REF':
      header.refs.push(s);
      break;
    case 'DTM':
      header.dtm.push(s);
      break;
    default:
      header.other.push(s);
      break;
  }
}

function attachNameLoopSegment(loop: NameLoop844, s: EdiSegment): void {
  switch (s.id) {
    case 'N2':
      loop.n2 = s;
      break;
    case 'N3':
      loop.n3 = s;
      break;
    case 'N4':
      loop.n4 = s;
      break;
    case 'N5':
      loop.n5 = s;
      break;
    case 'N6':
      loop.n6 = s;
      break;
    case 'N9':
      loop.n9.push(s);
      break;
    case 'REF':
      loop.refs.push(s);
      break;
    case 'PER':
      loop.per = s;
      break;
    default:
      loop.other.push(s);
      break;
  }
}

function attachItemSegment(item: ItemLoop844, s: EdiSegment): void {
  switch (s.id) {
    case 'QTY':
      item.qty.push(s);
      break;
    case 'AMT':
      item.amt.push(s);
      break;
    case 'CTP':
      item.ctp = s;
      break;
    default:
      item.other.push(s);
      break;
  }
}

function emptyHeader(): Header844 {
  return {refs: [], dtm: [], other: []};
}

function emptyItem(pad: EdiSegment): ItemLoop844 {
  return {pad, qty: [], amt: [], other: []};
}

type Phase = 'header' | 'name' | 'item';

/**
 * Walks one 844 transaction's flat body segments (ST/SE excluded) into its
 * loop structure. Never throws — a segment that doesn't fit anywhere
 * sensible in the current loop is bucketed into that loop's `other` list
 * rather than the walk aborting; the validator is what reports on a
 * document that's missing or misordered.
 */
export function parse844Segments(segments: EdiSegment[]): Parsed844 {
  const header = emptyHeader();
  const nameLoops: NameLoop844[] = [];
  const items: ItemLoop844[] = [];

  let phase: Phase = 'header';
  let currentNameLoop: NameLoop844 | undefined;
  let currentItem: ItemLoop844 | undefined;

  for (const s of segments) {
    switch (s.id) {
      case 'BAA':
        header.baa = s;
        break;

      case 'CON':
        header.con = s;
        break;

      case 'N1':
        phase = 'name';
        currentNameLoop = {n1: s, n9: [], refs: [], other: []};
        nameLoops.push(currentNameLoop);
        break;

      case 'N2':
      case 'N3':
      case 'N4':
      case 'N5':
      case 'N6':
      case 'N9':
        if (phase === 'name' && currentNameLoop) {
          attachNameLoopSegment(currentNameLoop, s);
        }
        break;

      case 'PAD':
        phase = 'item';
        currentNameLoop = undefined;
        currentItem = emptyItem(s);
        items.push(currentItem);
        break;

      default:
        if (phase === 'header') {
          attachHeaderSegment(header, s);
        } else if (phase === 'name' && currentNameLoop) {
          attachNameLoopSegment(currentNameLoop, s);
        } else if (phase === 'item' && currentItem) {
          attachItemSegment(currentItem, s);
        }
        break;
    }
  }

  return {header, nameLoops, items};
}

export interface EdiTransactionWithParsed844 extends EdiTransaction {
  parsed844?: Parsed844;
}

export interface EdiGroupWithParsed844 extends Omit<EdiGroup, 'transactions'> {
  transactions: EdiTransactionWithParsed844[];
}

export interface EdiEnvelopeWithParsed844 extends Omit<EdiEnvelope, 'groups'> {
  groups: EdiGroupWithParsed844[];
}

export function attachParsed844(
  envelope: EdiEnvelope
): EdiEnvelopeWithParsed844 {
  return {
    ...envelope,
    groups: envelope.groups.map(group => ({
      ...group,
      transactions: group.transactions.map(transaction => ({
        ...transaction,
        parsed844:
          transaction.transactionSetCode === '844'
            ? parse844Segments(transaction.segments)
            : undefined,
      })),
    })),
  };
}
